<template>
  <component :is="cardComponent" :label="label" :menu="menu" v-bind="$attrs">
    <slot />
    <template #controls>
      <slot v-if="lazyEditing" name="controls" />
    </template>
  </component>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import type CardMenuDefinition from "@/components/controls/card/CardMenuDefinition";
import BmCollapsibleCard from "@/components/controls/card/BmCollapsibleCard.vue";
import BmCard from "@/components/controls/card/BmCard.vue";
import { useI18n } from "@/plugins/i18n";

/**
 * General purpose card that supports an 'editing' state as bound v-model.
 *
 * When 'editing' is true it will expand a control panel to display any controls included in a named slot called 'controls'.
 * If you don't want to use the editing state then simply bind 'true' into editing.
 */
export default defineComponent({
  name: "BmEditCard",
  model: {
    prop: "editing",
    event: "change",
  },
  props: {
    editing: Boolean,
    label: { type: String },
    readOnly: { type: Boolean, default: false, required: false },
    isCollapsible: { type: Boolean, required: false, default: false },
  },
  setup(props, context) {
    const { $m } = useI18n();
    const lazyEditing = ref(props.editing);

    const toggleEditable = () => {
      lazyEditing.value = !lazyEditing.value;
      context.emit("change", lazyEditing.value);
    };

    const cardComponent = computed(() => {
      return props.isCollapsible ? BmCollapsibleCard : BmCard;
    });

    watch(
      () => props.editing,
      (newVal) => {
        lazyEditing.value = newVal;
      }
    );

    return {
      menu: computed((): CardMenuDefinition => {
        return {
          menuItems: [
            {
              displayText: $m.edit,
              onClick: () => toggleEditable(),
              isVisible: () => !props.editing && !props.readOnly,
            },
          ],
        } as CardMenuDefinition;
      }),
      cardComponent,
      lazyEditing,
    };
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/colours"

.bm-card .bm-card-title
  background-color: $bm-color-background04
</style>
